// A small macOS-style calendar glyph for DATE columns: a red header strip
// showing the weekday and a white body showing the day-of-month number. The
// glyph is "dynamic" — it reflects whatever date the field currently holds
// (falling back to today when the value is empty/unparseable), so it sits
// next to a date input as a friendly read-out of the chosen day.

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

// Accepts a value the form may hold for a DATE/DATETIME column: an ISO
// string ("2026-06-04", "2026-06-04T10:00"), a Date, or empty. Returns a
// valid Date (today when nothing usable is given) plus whether the value
// was actually a real date (so we can mute the glyph for empty fields).
function parseDate(value: unknown): { date: Date; hasValue: boolean } {
  if (value == null || value === '') return { date: new Date(), hasValue: false }
  if (value instanceof Date && !isNaN(value.getTime())) {
    return { date: value, hasValue: true }
  }
  const s = String(value).trim()
  // Plain YYYY-MM-DD — construct in local time so the day number doesn't
  // shift across a timezone boundary (new Date("2026-06-04") is UTC).
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    if (!isNaN(d.getTime())) return { date: d, hasValue: true }
  }
  const d = new Date(s)
  if (!isNaN(d.getTime())) return { date: d, hasValue: true }
  return { date: new Date(), hasValue: false }
}

interface Props {
  value: unknown
  size?: number
  title?: string
}

export default function MacCalendarIcon({ value, size = 34, title }: Props) {
  const { date, hasValue } = parseDate(value)
  const weekday = WEEKDAYS[date.getDay()]
  const day = date.getDate()
  const headerH = Math.round(size * 0.34)
  const opacity = hasValue ? 1 : 0.45

  return (
    <div
      title={title ?? (hasValue ? date.toDateString() : 'No date selected')}
      aria-hidden
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: Math.round(size * 0.16),
        overflow: 'hidden',
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
        display: 'flex',
        flexDirection: 'column',
        userSelect: 'none',
        opacity,
      }}
    >
      <div
        style={{
          height: headerH,
          background: '#ff3b30',
          color: '#fff',
          fontSize: Math.max(7, Math.round(size * 0.2)),
          fontWeight: 700,
          letterSpacing: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
        }}
      >
        {weekday}
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#1c1c1e',
          fontSize: Math.round(size * 0.44),
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        {day}
      </div>
    </div>
  )
}
